import { CalendarRange, Heart, Wallet } from 'lucide-react';

/**
 * Две программы «лучших периодов» на одной странице-движке.
 *
 * Страница одна (components/periods/PeriodsCalculatorPage.jsx), различия
 * между «Любовью» и «Деньгами» живут здесь. Классы Tailwind записаны
 * ЦЕЛИКОМ и не собираются из кусков: сборщик вырезает всё, чего не видит в
 * исходнике, и класс, склеенный в рантайме, до стилей не доедет.
 *
 * Порог T и правила расчёта здесь не хранятся и храниться не должны — их
 * считает сервер (backend/periods/profiles.py).
 */

export const LOVE_PERIODS = {
  key: 'love',
  path: '/love-periods',
  endpoint: '/api/calculator/love-periods/calculate',
  seoPage: 'love',
  // Сфера колеса, которую показывает страница (ответ несёт обе).
  sphere: 'love',
  // Кнопка-переход на соседний калькулятор под списком недель.
  sibling: { path: '/money-periods', label: 'А когда придут деньги? →' },
  // Переход в «Партнёра» — новая программа на /mum.
  partner: { path: '/mum', label: 'Узнай, какой он — твой партнёр →', on: true },
  Icon: Heart,
  headerLabel: 'ЛЮБОВЬ И ЗАМУЖЕСТВО',
  h1: 'Любовь и замужество: твои лучшие периоды',
  lead: 'Колесо года: удачные и трудные недели для любви',
  submitLabel: 'Показать мои периоды',
  noTimeNote: 'Без точного времени колесо тоже посчитаем, а со временем рождения оно станет точнее.',
  goals: { start: 'love_start', result: 'love_result', pay: 'love_pay_click' },
  accent: {
    page: 'from-rose-50 via-white to-white',
    badge: 'bg-rose-500',
    ring: 'focus:border-rose-400 focus:ring-rose-400',
    checkbox: 'text-rose-600 focus:ring-rose-400',
    note: 'bg-rose-50 text-rose-900',
    submit: 'bg-rose-500 hover:bg-rose-600',
    hookBox: 'border-rose-200 bg-rose-50',
    label: 'text-rose-600',
    best: 'border-rose-200 bg-rose-50',
  },
};

export const MONEY_PERIODS = {
  // НЕ 'money': так называется другая, натальная программа «Где твои деньги»,
  // и её окно регистрации уже занимает place='money'. Ключ идёт в data-testid
  // страницы и в place цели signup_click — две программы под одним ключом
  // слиплись бы и в свипе, и в Метрике.
  key: 'money_periods',
  path: '/money-periods',
  endpoint: '/api/calculator/money-periods/calculate',
  seoPage: 'moneyPeriods',
  sphere: 'money',
  sibling: { path: '/love-periods', label: 'А что с любовью? →' },
  Icon: Wallet,
  headerLabel: 'КОГДА ПРИДУТ ДЕНЬГИ',
  h1: 'Деньги: твои лучшие периоды',
  lead: 'Колесо года: удачные и трудные недели для денег',
  submitLabel: 'Показать мои периоды',
  noTimeNote: 'Без точного времени колесо тоже посчитаем, а со временем рождения оно станет точнее.',
  goals: { start: 'money_start', result: 'money_result', pay: 'money_pay_click' },
  accent: {
    page: 'from-emerald-50 via-white to-white',
    badge: 'bg-emerald-500',
    ring: 'focus:border-emerald-400 focus:ring-emerald-400',
    checkbox: 'text-emerald-600 focus:ring-emerald-400',
    note: 'bg-emerald-50 text-emerald-900',
    submit: 'bg-emerald-500 hover:bg-emerald-600',
    hookBox: 'border-emerald-200 bg-emerald-50',
    label: 'text-emerald-600',
    best: 'border-emerald-200 bg-emerald-50',
  },
};

/**
 * Маркетинговый год «Твоего года»: «Твой 2027» на плитке главной и в
 * заголовке страницы (внутри — «Твой год»). С октября колесо уже почти
 * целиком про следующий год — и называем его следующим.
 */
export function yearLabel(now = new Date()) {
  return now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
}

/**
 * «Твой год» (/year) — общее колесо 52 недель по образу «Любви» и «Денег»:
 * та же страница и тот же вывод (YearWheel3), сфера «general» — все
 * транзиты к натальным точкам (backend/periods/general_year.py). Над кругом
 * переключатель «Общее · Любовь · Деньги»: две последние — готовые колёса
 * своих программ (их расчёт, доступ и покупка — свои).
 */
export const YEAR_PERIODS = {
  key: 'year',
  path: '/year',
  endpoint: '/api/calculator/year/calculate',
  seoPage: 'year',
  sphere: 'general',
  Icon: CalendarRange,
  headerLabel: 'ТВОЙ ГОД',
  get h1() { return `Твой ${yearLabel()}`; },
  lead: 'Колесо года: удачные и трудные недели, фоновые влияния и главные события — по твоей натальной карте',
  submitLabel: 'Показать мой год',
  // После расчёта — «Прошлый год / С сегодня / Следующий год» над кругом;
  // на круге — 7 самых сильных фоновых процессов, остальные по кнопке.
  periodNav: true,
  arcLimit: 7,
  noTimeNote: 'Без точного времени колесо тоже посчитаем, а со временем рождения оно станет точнее.',
  goals: { start: 'year_start', result: 'year_result', pay: 'year_pay_click' },
  accent: {
    page: 'from-indigo-50 via-white to-white',
    badge: 'bg-indigo-500',
    ring: 'focus:border-indigo-400 focus:ring-indigo-400',
    checkbox: 'text-indigo-600 focus:ring-indigo-400',
    note: 'bg-indigo-50 text-indigo-900',
    submit: 'bg-indigo-500 hover:bg-indigo-600',
    hookBox: 'border-indigo-200 bg-indigo-50',
    label: 'text-indigo-600',
    best: 'border-indigo-200 bg-indigo-50',
  },
  // Переключатель над кругом: «Общее» — своё колесо, остальные — программы.
  switcher: [
    { key: 'general', label: 'Общее' },
    { key: 'love', label: 'Любовь', program: LOVE_PERIODS },
    { key: 'money', label: 'Деньги', program: MONEY_PERIODS },
  ],
};

export default { LOVE_PERIODS, MONEY_PERIODS, YEAR_PERIODS };
